import React from 'react';
import { Box } from '@mui/material';

import type { DashboardRendererProps } from '../config/types';

import { DashboardGrid } from './DashboardGrid';
import { DashboardSkeleton } from './DashboardSkeleton';

/**
 * DashboardRenderer Component
 * 
 * A pure presentation component that renders dashboard cards based on configuration.
 * This component has no business logic - it simply maps over the cards array
 * and renders each component with its props.
 * 
 * This separation allows for:
 * - Easy testing of rendering logic
 * - Future enhancements like drag-and-drop reordering
 * - Different layouts based on screen size or user preferences
 */
export const DashboardRenderer: React.FC<DashboardRendererProps> = ({ config, children }) => {
  // Show loading skeleton while data is being fetched
  if (config.isLoading) {
    return <DashboardSkeleton />;
  }

  // Handle empty state (no cards to display)
  if (config.cards.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          color: 'text.secondary',
        }}
      >
        No cards to display
      </Box>
    );
  }

  return (
    <>
      <DashboardGrid>
        {/* Render each card based on configuration */}
        {config.cards.map(({ id, component: Component, props }) => (
          <Component key={id} {...(props || {})} />
        ))}
      </DashboardGrid>
      
      {/* Allow for additional children (like ComplianceBadges) outside the grid */}
      {children}
    </>
  );
};

export default DashboardRenderer;